import { inject, injectable } from "inversify";
import { VintomError } from "../../../api/model/error/VintomError";
import { PlayerControls } from "../../../model/playerConfiguration/PlayerControls";
import { Subtitle } from "../../../model/playerConfiguration/Subtitle";
import { QualityLevel } from "../../../model/qualityLevel/QualityLevel";
import { ScenarioPath } from "../../../model/scenarioPath/ScenarioPath";
import { SubtitlesText } from "../../../model/subtitles/SubtitlesText";
import { TYPES } from "../../di/types";
import { ControlsLayer } from "../../layer/ControlsLayer";
import { ControlsLayerObserver } from "../../layerObserver/ControlsLayerObserver";

export interface ControlsLayerManager {
  newScenarioElement(scenarioPath: ScenarioPath): void;
  setTotalTime(totalTime: number): void;
  setConfiguration(configuration: PlayerControls, isAdminMode: boolean): void;
  play(): void;
  pause(): void;
  loop();
  moveTo(time: number): void;
  updateBufferedTime(time: number): void;
  showLoader(loaderTrigger: string, delayInMilliseconds?: number): void;
  hideLoader(): void;
  isLoaderPending(): boolean;
  changeVolume(volume: number): void;
  changeIsMuted(isMuted: boolean): void;
  selectSubtitleSource(subtitle: Subtitle): void;
  displaySubtitles(subtitles: SubtitlesText): void;
  scenarioElementEnded(): void;
  userInteractionEnded(): void;
  userInteractedWithPlayer(): void;
  userClickedPlayer(): void;
  registerObserver(observer: ControlsLayerObserver): void;
  showPlayerInfoTooltip(x: number, y: number): void;
  showMessage(message: string | VintomError): void;
  // @deprecated
  updateBandwidth(bandwidth: number): void;
  updateQualityLevels(qualityLevels: QualityLevel[]): void;
}

@injectable()
export class ControlsLayerManagerImpl implements ControlsLayerManager {
  private loaderTimeout: NodeJS.Timeout;

  constructor(@inject(TYPES.layer.ControlsLayer) private layer: ControlsLayer) {}

  newScenarioElement(scenarioPath: ScenarioPath): void {
    const scenarioElement = scenarioPath.currentElement;
    if (scenarioElement.video && scenarioElement.video.subtitles) {
      this.layer.resetScenarioElement(scenarioPath, scenarioElement.video.subtitles);
    } else {
      this.layer.resetScenarioElement(scenarioPath);
    }
  }

  setTotalTime(totalTime: number): void {
    this.layer.setTotalScenarioTime(totalTime);
  }

  setConfiguration(configuration: PlayerControls, isAdminMode: boolean) {
    this.layer.setConfiguration(configuration, isAdminMode);
  }

  play() {
    this.layer.play();
  }

  pause() {
    this.layer.pause();
  }

  loop() {
    this.layer.loop();
  }

  moveTo(time: number): void {
    this.layer.moveTo(time);
  }

  updateBufferedTime(time: number): void {
    this.layer.updateBufferedTime(time);
  }

  showLoader(loaderTrigger: string, delayInMilliseconds?: number): void {
    this.clearLoaderTimeout();

    if (delayInMilliseconds <= 0) {
      this.layer.showLoader(loaderTrigger);
    } else {
      this.loaderTimeout = setTimeout(() => {
        this.layer.showLoader(loaderTrigger);
        this.clearLoaderTimeout();
      }, delayInMilliseconds);
    }
  }

  hideLoader(): void {
    this.clearLoaderTimeout();
    this.layer.hideLoader();
  }

  isLoaderPending(): boolean {
    return this.loaderTimeout != null;
  }

  registerObserver(observer: ControlsLayerObserver): void {
    this.layer.registerObserver(observer);
  }

  changeVolume(volume: number) {
    this.layer.setVolume(volume);
  }

  changeIsMuted(isMuted: boolean): void {
    this.layer.setIsMuted(isMuted);
  }

  selectSubtitleSource(subtitle: Subtitle): void {
    this.layer.selectSubtitleSource(subtitle);
  }

  displaySubtitles(subtitles: SubtitlesText): void {
    this.layer.displaySubtitles(subtitles);
  }

  scenarioElementEnded() {
    this.layer.scenarioEnded();
  }

  userInteractionEnded() {
    this.layer.userInteractionEnded();
  }

  userInteractedWithPlayer() {
    this.layer.userInteractedWithPlayer();
  }

  userClickedPlayer() {
    this.layer.userClickedPlayer();
  }

  showPlayerInfoTooltip(x: number, y: number) {
    this.layer.showPlayerInfoTooltip(x, y);
  }

  showMessage(message: string | VintomError): void {
    this.layer.showMessage(message);
  }

  updateBandwidth(bandwidth: number): void {
    this.layer.setBandwidth(bandwidth);
  }

  updateQualityLevels(qualityLevels: QualityLevel[]): void {
    this.layer.setQualityLevels(qualityLevels);
  }

  private clearLoaderTimeout() {
    clearTimeout(this.loaderTimeout);

    this.loaderTimeout = null;
  }
}
